import { useGetGeolocalizzazioneComuni } from '@framework/api-client/api/inquiries/geolocalizzazione-controller/geolocalizzazione-controller.ts';
import { type Provincia } from '@framework/api-client/api/inquiries/schemas/provincia.ts';
import { useExpressionValue } from '@framework/engine/schema/expressions/render/hooks/useExpressionValue.tsx';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { getWidgetProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { useField } from '@framework/form/field/useField.tsx';
import { Alert } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type Maybe } from 'yup';
import { ProvinceWidgetSelectComponent } from './components/ProvinceWidgetSelectComponent.tsx';
import {
  getProvinceDataFieldName,
  getProvinceFieldName,
  getTownDataFieldName,
  getTownFieldName,
} from './createProvinceWidgetSchema.ts';
import { type IProvinceWidget } from './IProvinceWidget.ts';
import { mapProvinceList } from './utils/mapProvinceList.ts';
import { ProvinceWidgetLogic } from './utils/ProvinceWidgetLogic.ts';

export type IProvinceWidgetComponentProps =
  IWidgetComponentProps<IProvinceWidget>;

export function ProvinceWidgetComponent(props: IProvinceWidgetComponentProps) {
  const { data, isLoading, error } = useGetGeolocalizzazioneComuni(
    {
      listaCodiceGruppo: props.widget.municipalityGroupList ?? [],
    },
    { request: { arrayFormat: 'repeat' } },
  );

  const { t } = useTranslation();
  const options = useMemo(() => mapProvinceList(data), [data]);

  const widgetOptions = getWidgetProps(props);

  const defaultProvinceResult = useExpressionValue(
    widgetOptions.name,
    props.widget.defaultProvince,
  );
  const defaultTownResult = useExpressionValue(
    widgetOptions.name,
    props.widget.defaultTown,
  );

  const provinceFieldName = getProvinceFieldName(widgetOptions.name);
  const provinceDataFieldName = getProvinceDataFieldName(widgetOptions.name);
  const townFieldName = getTownFieldName(widgetOptions.name);
  const townDataFieldName = getTownDataFieldName(widgetOptions.name);

  const province = useWatch({ name: provinceFieldName }) as Maybe<string>;
  const provinceDataField = useField<Provincia | null | undefined>(
    provinceDataFieldName,
  );
  const provinceField = useField(provinceFieldName);

  const town = useWatch({ name: townFieldName }) as Maybe<string>;
  const townDataField = useField<Provincia | null | undefined>(
    townDataFieldName,
  );
  const townField = useField(townFieldName);

  const isProvinceDirty = provinceField.dirty.length > 0;
  const isTownDirty = townField.dirty.length > 0;

  const selectedProvinceRef = useRef(province);

  /**
   * Hook che inizializza i dati del widget, andando a caricare
   * i default value, che per questo widget sono campi custom
   * (non utilizza il campo defaultValue come gli altri widget)
   */
  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (error) {
      return;
    }

    // Estraggo i valori
    const computedValues = ProvinceWidgetLogic.getDefaultValue({
      defaultValue: {
        province:
          defaultProvinceResult.type === 'value' &&
          typeof defaultProvinceResult.result === 'string'
            ? defaultProvinceResult.result
            : null,
        town:
          defaultTownResult.type === 'value' &&
          typeof defaultTownResult.result === 'string'
            ? defaultTownResult.result
            : null,
      },
      current: {
        province: provinceField.getValue(),
        town: townField.getValue(),
      },
      options,
    });

    if (
      computedValues.province &&
      computedValues.province.codiceBelfiore !== province &&
      !isProvinceDirty
    ) {
      provinceField.setValue(computedValues.province.codiceBelfiore, {
        shouldDirty: false,
      });
      provinceDataField.setValue(computedValues.province, {
        shouldDirty: false,
      });
      selectedProvinceRef.current = computedValues.province.codiceBelfiore;
    } else if (
      computedValues.province &&
      computedValues.province.codiceBelfiore === province &&
      provinceDataField.getValue()?.codiceBelfiore !==
        computedValues.province.codiceBelfiore
    ) {
      // Il codice è già valorizzato (e.g. default applicato dal cast dello
      // schema) ma i dati estesi sono vuoti o stantii: li riallineo,
      // perché alimentano campi calcolati e idratazione.
      provinceDataField.setValue(computedValues.province, {
        shouldDirty: false,
      });
    }

    if (
      computedValues.town &&
      computedValues.town.codiceBelfiore !== town &&
      !isTownDirty
    ) {
      townField.setValue(computedValues.town.codiceBelfiore, {
        shouldDirty: false,
      });
      townDataField.setValue(computedValues.town, { shouldDirty: false });
    } else if (
      computedValues.town &&
      computedValues.town.codiceBelfiore === town &&
      townDataField.getValue()?.codiceBelfiore !==
        computedValues.town.codiceBelfiore
    ) {
      // Come sopra, riallineo i dati estesi del comune
      townDataField.setValue(computedValues.town, { shouldDirty: false });
    }

    if (
      selectedProvinceRef.current !== computedValues.province?.codiceBelfiore
    ) {
      // Se il riferimento non è ancora inizializzato (`null` o `undefined`,
      // i valori possono arrivare dopo il mount) sincronizzo soltanto,
      // senza interpretarlo come un cambio di provincia.
      if (selectedProvinceRef.current != null) {
        townField.setValue(null, { shouldDirty: false });
        townDataField.setValue(null, { shouldDirty: false });
      }
      selectedProvinceRef.current = computedValues.province?.codiceBelfiore;
    }
  }, [
    data,
    error,
    isLoading,
    options,
    options.provinces,
    options.towns,
    province,
    provinceDataField,
    provinceField,
    isProvinceDirty,
    isTownDirty,
    defaultProvinceResult,
    defaultTownResult,
    town,
    townDataField,
    townField,
  ]);

  if (error) {
    return (
      <Alert
        message={t('province-widget.error', 'Errore')}
        description={t(
          'province-widget.error-description',
          'Errore durante il caricamento dei dati delle province. Riprovare più tardi.',
        )}
        type="error"
        showIcon
      />
    );
  }

  return (
    <ProvinceWidgetSelectComponent
      {...props}
      loading={isLoading}
      provinces={options.provinces}
      towns={options.towns}
    />
  );
}
